import { useMediaQuery } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import clsx from "clsx";
import React, { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useNavigate } from "react-router";
import { startLoading, stopLoading } from "../../actions/loadingAction";
import { Endpoint, Status } from "../../apis/apiConstants";
import AjaxHelper from "../../apis/apiRequest";
import LoadingAnimation from "../../components/LoadingAnimation";
import AppFooter from "../../components/appFooter";
import SideDrawer from "../../components/sideDrawer";
import SideLeftToolbar from "../../components/sideLeftToolbar";
import {
  DrawerItemsKey,
  PATH,
  ResponsiveDialogActions,
  TITLE,
} from "../../constants/appConstants";
import SessionDetails from "../../helpers/sessionDetails";
import { setResponsiveDialog } from "../../reducers/responsiveDialogReducer";
import routes from "../../routes";
import Home from "../home";
import "./dashboard.scss";

const drawerWidth = 240;

const HIDE_FOOTER_PAGES = [DrawerItemsKey.FAMILY_TREE];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  contentMobile: {
    flexGrow: 1,
    padding: theme.spacing(0),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "100vh",
  },
  content: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "100vh",
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
}));

const loading = () => (
  <div className="animated fadeIn pt-3 text-center">Loading...</div>
);

export default function Dashboard(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const [toolbarTitle, setToolbarTitle] = React.useState("Home");
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [currentPage, setCurrentPage] = useState("dashboard");

  useEffect(() => {
    getUserData();
    navigate(PATH.HOME_SCREEN, { replace: true });
  }, [props]);

  const getUserData = () => {
    dispatch(startLoading);
    AjaxHelper.getApi(navigate, Endpoint.GET_USER)
      .then((response) => {
        dispatch(stopLoading);
        if (response.data.status === Status.API_SUCCESS) {
          let userData = response.data.data;
          SessionDetails.saveCurrentUser(userData);
        }
      })
      .catch((err) => {
        dispatch(stopLoading);
      });
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleOnClickMenuItems = () => {
    if (isSmallScreen) {
      setOpen(false);
    }
  };

  const isFooterVisible = () => {
    return !HIDE_FOOTER_PAGES.includes(currentPage);
  };

  const handleMenuClick = (menu, params) => {
    console.log(menu);
    setCurrentPage(menu);
    switch (menu) {
      case DrawerItemsKey.DASHBOARD_SCREEN:
        navigate(PATH.HOME_SCREEN, { replace: true });
        setToolbarTitle(TITLE.HOME_SCREEN);
        break;
      case DrawerItemsKey.APPLICANTS_SCREEN:
        navigate(PATH.APPLICANTS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.APPLICANTS_SCREEN);
        break;
      case DrawerItemsKey.MEMBERS_SCREEN:
        navigate(PATH.MEMBERS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.MEMBERS_SCREEN);
        break;
      case DrawerItemsKey.JAMAATS_SCREEN:
        navigate(PATH.JAMAATS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.JAMAATS_SCREEN);
        break;
      case DrawerItemsKey.RESERVED_CODES_SCREEN:
        navigate(PATH.RESERVED_CODES_SCREEN, { replace: true });
        setToolbarTitle(TITLE.RESERVED_CODES_SCREEN);
        break;
      case DrawerItemsKey.CITY_SCREEN:
        navigate(PATH.CITY_SCREEN, { replace: true });
        setToolbarTitle(TITLE.CITY_SCREEN);
        break;
      case DrawerItemsKey.PROFILE_SCREEN:
        navigate(PATH.PROFILE_SCREEN, { replace: true });
        setToolbarTitle(TITLE.PROFILE_SCREEN);
        break;
      case DrawerItemsKey.NOTIFICATIONS_SCREEN:
        navigate(PATH.NOTIFICATIONS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.NOTIFICATIONS_SCREEN);
        break;
      case DrawerItemsKey.NOTIFICATION_LIST_SCREEN:
        navigate(PATH.NOTIFICATION_LIST_SCREEN, { replace: true });
        setToolbarTitle(TITLE.NOTIFICATION_LIST_SCREEN);
        break;
      case DrawerItemsKey.FAMILY_TREE:
        navigate(PATH.FAMILY_TREE, { replace: true });
        setToolbarTitle(TITLE.FAMILY_TREE);
        break;
      case DrawerItemsKey.APPROVALS_SCREEN:
        navigate(PATH.APPROVALS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.APPROVALS_SCREEN);
        break;
      case DrawerItemsKey.REQUESTS:
        navigate(PATH.REQUESTS, { replace: true });
        setToolbarTitle(TITLE.REQUESTS);
        break;
      case DrawerItemsKey.NEWS_SCREEN:
        navigate(PATH.NEWS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.NEWS_SCREEN);
        break;
      case DrawerItemsKey.STORIES_SCREEN:
        navigate(PATH.STORIES_SCREEN, { replace: true });
        setToolbarTitle(TITLE.STORIES_SCREEN);
        break;
      case DrawerItemsKey.ADD_STORIES_SCREEN:
        navigate(PATH.ADD_STORIES_SCREEN, { replace: false });
        setToolbarTitle(TITLE.ADD_STORIES_SCREEN);
        break;
      case DrawerItemsKey.ADD_NEWS_SCREEN:
        navigate(PATH.ADD_NEWS_SCREEN, { replace: false });
        setToolbarTitle(TITLE.ADD_NEWS_SCREEN);
        break;
      case DrawerItemsKey.UPDATE_NEWS_SCREEN:
        navigate(PATH.UPDATE_NEWS_SCREEN, { replace: false, state: params });
        setToolbarTitle(TITLE.UPDATE_NEWS_SCREEN);
        break;
      case DrawerItemsKey.UPDATE_STORY_SCREEN:
        navigate(PATH.UPDATE_STORY_SCREEN, { replace: false, state: params });
        setToolbarTitle(TITLE.UPDATE_STORY_SCREEN);
        break;
      case DrawerItemsKey.NEWS_APPROVALS_SCREEN:
        navigate(PATH.NEWS_APPROVALS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.NEWS_APPROVALS_SCREEN);
        break;
      case DrawerItemsKey.NEWS_DETAILS_SCREEN:
        navigate(PATH.NEWS_DETAILS_SCREEN, { replace: false, state: params });
        setToolbarTitle(TITLE.NEWS_DETAILS_SCREEN);
        break;
      case DrawerItemsKey.ASK_HELP:
        navigate(PATH.ASK_HELP, { replace: true, state: params });
        setToolbarTitle(TITLE.ASK_HELP);
        break;
      case DrawerItemsKey.HELPLINE_CONTACTS:
        navigate(PATH.HELPLINE_CONTACTS, { replace: true, state: params });
        setToolbarTitle(TITLE.HELPLINE_CONTACTS);
        break;
      case DrawerItemsKey.AWARDS_ADMIN:
        navigate(PATH.AWARDS_ADMIN, { replace: true });
        setToolbarTitle(TITLE.AWARDS_ADMIN);
        break;
      case DrawerItemsKey.AWARDS:
        navigate(PATH.AWARDS, { replace: true });
        setToolbarTitle(TITLE.AWARDS);
        break;
      case DrawerItemsKey.EVENTS_SCREEN:
        navigate(PATH.EVENTS_SCREEN, { replace: true });
        setToolbarTitle(TITLE.EVENTS_SCREEN);
        break;
      case DrawerItemsKey.STORIES_DETAILS_SCREEN:
        navigate(PATH.STORIES_DETAILS_SCREEN, {
          replace: false,
          state: params,
        });
        setToolbarTitle(TITLE.STORIES_DETAILS_SCREEN);
        break;
      case DrawerItemsKey.HELPLINE_DETAILS:
        navigate(PATH.HELPLINE_DETAILS, {
          replace: false,
          state: params,
        });
        setToolbarTitle(TITLE.HELPLINE_DETAILS);
        break;
      case DrawerItemsKey.LOGOUT:
        dispatch(
          setResponsiveDialog(
            true,
            "Logout",
            "Do you want to logout?",
            "Logout",
            "Cancel",
            (action) => {
              if (action === ResponsiveDialogActions.POSITIVE) {
                SessionDetails.clearStoredData();
                navigate(PATH.LOGIN_SCREEN, { replace: true });
              }
            }
          )
        );
        break;
      default:
        break;
    }
    handleOnClickMenuItems();
  };

  const route = () => {
    return (
      <Routes>
        {routes.map((route, idx) => {
          return route.component ? (
            route.path === PATH.LOGIN_SCREEN ? (
              <Route to="/home/dashboard" />
            ) : (
              <Route
                key={idx}
                path={route.path}
                element={<route.component onPageChange={handleMenuClick} />}
              />
            )
          ) : null;
        })}
        <Route to={"home"} element={<Home />} />
      </Routes>
    );
  };

  return (
    <div className={classes.root}>
      {useSelector((state) => state.loadingReducer.loading) ? (
        <LoadingAnimation />
      ) : null}

      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <SideLeftToolbar
          title={toolbarTitle}
          handleDrawerOpen={handleDrawerOpen}
          open={open}
        />
      </AppBar>
      <SideDrawer
        handleDrawerClose={handleDrawerClose}
        handleMenuClick={handleMenuClick}
        open={open}
      />
      <main
        className={
          isSmallScreen
            ? clsx(classes.contentMobile)
            : clsx(classes.content, {
                [classes.contentShift]: open,
              })
        }
      >
        <div style={{ padding: theme.spacing(0) }}>
          <div className={classes.drawerHeader} />
          <Suspense fallback={loading()}>{route()}</Suspense>
        </div>
        {isFooterVisible() ? <AppFooter /> : null}
      </main>
    </div>
  );
}
